import { useRef } from 'react';
import { ArrowUpRightIcon } from '@phosphor-icons/react';
import { Reveal } from '@/components/reveal';
import { Section, shellClass } from '@/components/section';
import { SectionHeader, playHeader } from '@/components/section-header';
import { Button } from '@/components/ui/button';
import { copy } from '@/content/copy';
import { CONTACT_URL, DOCS_URL, HAS_CONTACT_DESTINATION, isExternalUrl } from '@/lib/env';
import { useRevealOnEnter } from '@/scroll/use-reveal-on-enter';

const { contact } = copy;

/**
 * Closing CTA, carrying the `#contact` anchor. The panel is a lit field with concentric coin
 * rings off its right edge. "Partner with us" opens the partner form (in a new tab) and
 * appears whenever the contact URL points beyond this section; when VITE_CONTACT_URL is
 * `#contact` the documentation link is the one action.
 */
export function Contact() {
  const ref = useRef<HTMLElement>(null);

  useRevealOnEnter(ref, (tl, q) => {
    tl.to(q('[data-panel]'), { opacity: 1, y: 0, duration: 0.9 }, 0);
    const at = playHeader(tl, q, 0.2);
    tl.to(q('[data-step]'), { opacity: 1, y: 0, duration: 0.6, stagger: 0.1 }, at).to(
      q('[data-actions]'),
      { opacity: 1, y: 0, duration: 0.6 },
      at + 0.2,
    );
  });

  return (
    <Section ref={ref} id="contact">
      <div className={shellClass}>
        <Reveal
          data-panel
          className="cta-field relative overflow-hidden rounded-[28px] border border-brand-sky/20 px-[clamp(1.6rem,5vw,4rem)] py-[clamp(2.6rem,6vw,4.5rem)]"
        >
          <div aria-hidden className="cta-rings absolute inset-0" />
          <div className="relative grid gap-9 lg:grid-cols-[1.3fr_auto] lg:items-end lg:gap-12">
            <div className="grid gap-8">
              <SectionHeader eyebrow={contact.eyebrow} title={contact.title} lede={contact.lede} />
              {/* What happens next; the list reads the same with or without a contact link. */}
              <ol
                aria-label={contact.steps.label}
                className="m-0 grid list-none gap-6 p-0 sm:grid-cols-3 sm:gap-8"
              >
                {contact.steps.items.map((step, index) => (
                  <li key={step.title}>
                    <Reveal data-step className="grid gap-2 border-t border-foreground/15 pt-4">
                      <span aria-hidden className="hud text-brand-sky">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="text-sm font-semibold">{step.title}</span>
                      <span className="text-xs leading-relaxed text-muted-foreground">
                        {step.body}
                      </span>
                    </Reveal>
                  </li>
                ))}
              </ol>
            </div>
            <Reveal data-actions className="flex flex-wrap items-center gap-3">
              {HAS_CONTACT_DESTINATION ? (
                <Button
                  href={CONTACT_URL}
                  target={isExternalUrl(CONTACT_URL) ? '_blank' : undefined}
                  icon={<ArrowUpRightIcon size={16} weight="bold" aria-hidden />}
                >
                  {contact.primary}
                </Button>
              ) : null}
              <Button href={DOCS_URL} variant={HAS_CONTACT_DESTINATION ? 'ghost' : 'primary'}>
                {contact.secondary}
              </Button>
            </Reveal>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
